import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Upload, RefreshCw, ImageIcon, AlertTriangle, Type, Calendar, Palette, Globe, Lock, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Month names for dropdown
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_EL = ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'];

// Short month names for date display
const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_SHORT_EL = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαι', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];

// Get all 12 possible 2-month period options
const getPeriodOptions = (language) => {
  const months = language === 'el' ? MONTHS_EL : MONTHS_EN;
  const options = [];
  for (let i = 0; i < 12; i++) {
    const startMonth = i + 1;
    const endMonth = (i + 1) % 12 + 1;
    const endMonthName = i === 11 ? months[0] : months[i + 1];
    options.push({
      value: `${startMonth}-${endMonth}`,
      label: `${months[i]} - ${endMonthName}`
    });
  }
  return options;
};

// Format a date for display
const formatDateDisplay = (dateStr, language) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = (language === 'el' ? MONTHS_SHORT_EL : MONTHS_SHORT_EN)[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export default function AdminSettings() {
  const { t, language } = useLanguage();
  const { ladder, players, updateLadder, uploadLogo, resetLadder } = useLadder();
  const { branding, refreshBranding, getAssetUrl } = useBranding();
  const { isSuperAdmin, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: ladder?.name || '',
    subtitle: ladder?.subtitle || ''
  });
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [periodType, setPeriodType] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [defendingChampionId, setDefendingChampionId] = useState('');
  const fileInputRef = useRef(null);
  
  // Branding state
  const [brandingForm, setBrandingForm] = useState({
    accent_color: '#1e3a5f',
    theme_color: '#1e3a5f',
    site_name: '',
    site_title: '',
    site_description: '',
    site_url: '',
    keywords: ''
  });
  
  // Asset refs
  const faviconInputRef = useRef(null);
  const ogImageInputRef = useRef(null);
  const pwaIcon192InputRef = useRef(null);
  const pwaIcon512InputRef = useRef(null);

  // Initialize from ladder and branding
  useEffect(() => {
    if (ladder?.period_start_date && ladder?.period_end_date) {
      setPeriodType('date');
      setStartDate(ladder.period_start_date);
      setEndDate(ladder.period_end_date);
    } else if (ladder?.period_start_month && ladder?.period_end_month) {
      setPeriodType('month');
      setSelectedPeriod(`${ladder.period_start_month}-${ladder.period_end_month}`);
    } else {
      setPeriodType('month');
      const now = new Date();
      const month = now.getMonth() + 1;
      const startMonth = month % 2 === 0 ? month - 1 : month;
      setSelectedPeriod(`${startMonth}-${startMonth + 1}`);
    }
    // Initialize defending champion
    if (ladder?.defending_champion_id) {
      setDefendingChampionId(ladder.defending_champion_id);
    }
  }, [ladder]);

  // Initialize branding form from API data
  useEffect(() => {
    if (branding) {
      setBrandingForm({
        accent_color: branding.accent_color || '#1e3a5f',
        theme_color: branding.theme_color || branding.accent_color || '#1e3a5f',
        site_name: branding.site_name || branding.name || '',
        site_title: branding.site_title || branding.name || '',
        site_description: branding.site_description || '',
        site_url: branding.site_url || '',
        keywords: branding.keywords || ''
      });
    }
  }, [branding]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('leagueTitleRequired'));
      return;
    }
    setLoading(true);
    try {
      await updateLadder(ladder.id, { name: form.name, subtitle: form.subtitle });
      toast.success(t('settingsSaved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodSave = async () => {
    setLoading(true);
    try {
      if (periodType === 'date') {
        if (!startDate || !endDate) {
          toast.error(language === 'el' ? 'Παρακαλώ επιλέξτε ημερομηνίες' : 'Please select dates');
          setLoading(false);
          return;
        }
        if (new Date(startDate) >= new Date(endDate)) {
          toast.error(language === 'el' ? 'Η ημερομηνία λήξης πρέπει να είναι μετά την αρχή' : 'End date must be after start date');
          setLoading(false);
          return;
        }
        await updateLadder(ladder.id, { 
          period_start_date: startDate, 
          period_end_date: endDate,
          period_start_month: null,
          period_end_month: null
        });
      } else {
        if (!selectedPeriod) {
          setLoading(false);
          return;
        }
        const [startMonth, endMonth] = selectedPeriod.split('-').map(Number);
        await updateLadder(ladder.id, { 
          period_start_month: startMonth, 
          period_end_month: endMonth,
          period_start_date: null,
          period_end_date: null
        });
      }
      toast.success(t('periodSaved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setLoading(true);
    try {
      const logoUrl = await uploadLogo(file);
      await updateLadder(ladder.id, { club_logo: logoUrl });
      toast.success(t('settingsSaved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    try {
      await resetLadder(ladder.id);
      toast.success(t('ladderReset'));
      setResetDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  // ============ BRANDING HANDLERS (Super Admin Only) ============

  const handleBrandingSave = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/branding`,
        {
          accent_color: brandingForm.accent_color,
          theme_color: brandingForm.theme_color,
          site_name: brandingForm.site_name,
          site_title: brandingForm.site_title,
          site_description: brandingForm.site_description,
          site_url: brandingForm.site_url,
          keywords: brandingForm.keywords
        },
        { headers: getAuthHeaders() }
      );
      refreshBranding();
      toast.success(language === 'el' ? 'Branding αποθηκεύτηκε' : 'Branding saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssetUpload = async (e, assetType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/x-icon', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (PNG, JPG, ICO, WebP, GIF)');
      return;
    }

    // Size limits
    const sizeLimits = {
      'favicon': 500 * 1024,
      'og-image': 2 * 1024 * 1024,
      'pwa-192': 500 * 1024,
      'pwa-512': 1 * 1024 * 1024
    };

    if (file.size > sizeLimits[assetType]) {
      toast.error(`File too large (max ${sizeLimits[assetType] / 1024}KB)`);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API_URL}/api/branding/upload/${assetType}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      refreshBranding();
      toast.success(language === 'el' ? 'Αρχείο ανέβηκε' : 'File uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const periodOptions = getPeriodOptions(language);

  if (!ladder) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Settings className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">{t('settings')}</h1>
          <p className="text-sm text-muted-foreground">
            {language === 'el' ? 'Διαχείριση ρυθμίσεων ladder' : 'Manage ladder settings'}
          </p>
        </div>
      </div>

      <div className="grid gap-6">
        {/* League Title & Subtitle */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-muted">
              <Type className="h-5 w-5 text-foreground" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {t('leagueTitle')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('title')}</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'el' ? 'π.χ. Xanthi Padel League' : 'e.g. Xanthi Padel League'}
                data-testid="league-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subtitle">{t('subtitle')}</Label>
              <Input
                id="subtitle"
                value={form.subtitle}
                onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder={language === 'el' ? 'π.χ. Intermediate' : 'e.g. Intermediate'}
                data-testid="league-subtitle-input"
              />
            </div>
            <Button onClick={handleSave} disabled={loading} data-testid="save-title-button">
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </div>

        {/* Period Configuration */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-muted">
              <Calendar className="h-5 w-5 text-foreground" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {t('period')}
            </h2>
          </div>
          
          <div className="space-y-4">
            {/* Period Type Toggle */}
            <div className="flex gap-2">
              <Button 
                variant={periodType === 'month' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setPeriodType('month')}
              >
                {language === 'el' ? 'Μήνες' : 'Months'}
              </Button>
              <Button 
                variant={periodType === 'date' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setPeriodType('date')}
              >
                {language === 'el' ? 'Ημερομηνίες' : 'Dates'}
              </Button>
            </div>

            {periodType === 'month' ? (
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Δίμηνο' : 'Two-Month Period'}</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger data-testid="period-select">
                    <SelectValue placeholder={language === 'el' ? 'Επιλέξτε περίοδο' : 'Select period'} />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Ημ. Έναρξης' : 'Start Date'}</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Ημ. Λήξης' : 'End Date'}</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <Button onClick={handlePeriodSave} disabled={loading}>
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </div>

        {/* Club Logo */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-muted">
              <ImageIcon className="h-5 w-5 text-foreground" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {t('clubLogo')}
            </h2>
          </div>
          
          <div className="flex items-start gap-6">
            <div className="h-24 w-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted">
              {ladder?.club_logo ? (
                <img src={ladder.club_logo} alt="Club Logo" className="h-full w-full object-contain" />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-3">
                {language === 'el' 
                  ? 'Ανεβάστε το λογότυπο του club. Προτεινόμενο μέγεθος: 200x200px. Μέγιστο 5MB.'
                  : 'Upload your club logo. Recommended size: 200x200px. Max 5MB.'}
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                {t('uploadLogo')}
              </Button>
            </div>
          </div>
        </div>

        {/* Defending Champion */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Crown className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {t('defendingChampion')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {language === 'el' 
                ? 'Επιλέξτε τον νικητή της προηγούμενης περιόδου. Θα εμφανίζεται με το σύμβολο Ⓒ στο leaderboard.'
                : 'Select the winner of the previous period. They will be displayed with the Ⓒ symbol on the leaderboard.'}
            </p>
            
            <div className="space-y-2">
              <Label>{t('player')}</Label>
              <Select 
                value={defendingChampionId || 'none'} 
                onValueChange={(val) => setDefendingChampionId(val === 'none' ? '' : val)}
              >
                <SelectTrigger data-testid="defending-champion-select">
                  <SelectValue placeholder={language === 'el' ? 'Επιλέξτε παίκτη...' : 'Select player...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {language === 'el' ? '— Κανένας —' : '— None —'}
                  </SelectItem>
                  {players
                    .filter(p => p.is_active !== false)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((player) => (
                      <SelectItem key={player.id} value={player.id}>
                        {player.display_name || player.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={async () => {
                setLoading(true);
                try {
                  await updateLadder(ladder.id, { defending_champion_id: defendingChampionId || null });
                  toast.success(language === 'el' ? 'Champion αποθηκεύτηκε' : 'Champion saved');
                } catch (error) {
                  toast.error(error.response?.data?.detail || t('error'));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              data-testid="save-champion-button"
            >
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </div>

        {/* ============ BRANDING SECTION (Super Admin Only) ============ */}
        {isSuperAdmin ? (
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-muted">
                <Palette className="h-5 w-5 text-foreground" />
              </div>
              <h2 className="font-heading text-lg font-semibold text-foreground">
                {language === 'el' ? 'Branding & Meta Tags' : 'Branding & Meta Tags'}
              </h2>
              <span className="ml-auto text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-1 rounded">
                Super Admin
              </span>
            </div>
            
            <div className="space-y-6">
              {/* Colors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Accent Color' : 'Accent Color'}</Label>
                  <div className="flex gap-2">
                    <div 
                      className="h-10 w-10 rounded border cursor-pointer"
                      style={{ backgroundColor: brandingForm.accent_color }}
                      onClick={() => document.getElementById('accent-color').click()}
                    >
                      <input
                        id="accent-color"
                        type="color"
                        value={brandingForm.accent_color}
                        onChange={(e) => setBrandingForm(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="opacity-0 w-full h-full cursor-pointer"
                      />
                    </div>
                    <Input
                      value={brandingForm.accent_color}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="font-mono flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Theme Color (PWA)' : 'Theme Color (PWA)'}</Label>
                  <div className="flex gap-2">
                    <div 
                      className="h-10 w-10 rounded border cursor-pointer"
                      style={{ backgroundColor: brandingForm.theme_color }}
                      onClick={() => document.getElementById('theme-color').click()}
                    >
                      <input
                        id="theme-color"
                        type="color"
                        value={brandingForm.theme_color}
                        onChange={(e) => setBrandingForm(prev => ({ ...prev, theme_color: e.target.value }))}
                        className="opacity-0 w-full h-full cursor-pointer"
                      />
                    </div>
                    <Input
                      value={brandingForm.theme_color}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, theme_color: e.target.value }))}
                      className="font-mono flex-1"
                    />
                  </div>
                </div>
              </div>

              <hr className="border-border" />

              {/* Meta Tags */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {language === 'el' ? 'Meta Tags (SEO & Social)' : 'Meta Tags (SEO & Social)'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site Name</Label>
                    <Input
                      value={brandingForm.site_name}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, site_name: e.target.value }))}
                      placeholder="Xanthi Padel League"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Site Title</Label>
                    <Input
                      value={brandingForm.site_title}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, site_title: e.target.value }))}
                      placeholder="Xanthi Padel League - Intermediate"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Site URL</Label>
                  <Input
                    value={brandingForm.site_url}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, site_url: e.target.value }))}
                    placeholder="https://xanthi.phidel.gr"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={brandingForm.site_description}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, site_description: e.target.value }))}
                    placeholder="Live rankings and match results for Xanthi Padel League"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Keywords</Label>
                  <Input
                    value={brandingForm.keywords}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, keywords: e.target.value }))}
                    placeholder="padel, ladder, xanthi, competition"
                  />
                </div>
              </div>

              <Button onClick={handleBrandingSave} disabled={loading}>
                {loading ? t('loading') : (language === 'el' ? 'Αποθήκευση Branding' : 'Save Branding')}
              </Button>

              <hr className="border-border" />

              {/* Asset Uploads */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  {language === 'el' ? 'Assets (Favicon, OG Image, PWA Icons)' : 'Assets (Favicon, OG Image, PWA Icons)'}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Favicon */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded border flex items-center justify-center bg-muted">
                        {branding?.favicon_url ? (
                          <img src={getAssetUrl(branding.favicon_url)} alt="Favicon" className="h-6 w-6" />
                        ) : (
                          <span className="text-xs text-muted-foreground">ICO</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">Favicon</p>
                        <p className="text-xs text-muted-foreground">32x32px, max 500KB</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={faviconInputRef}
                      onChange={(e) => handleAssetUpload(e, 'favicon')}
                      accept="image/*,.ico"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => faviconInputRef.current?.click()}
                      disabled={loading}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Upload
                    </Button>
                  </div>

                  {/* OG Image */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-16 rounded border flex items-center justify-center bg-muted overflow-hidden">
                        {branding?.og_image_url ? (
                          <img src={getAssetUrl(branding.og_image_url)} alt="OG" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs text-muted-foreground">OG</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">OG Image</p>
                        <p className="text-xs text-muted-foreground">1200x630px, max 2MB</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={ogImageInputRef}
                      onChange={(e) => handleAssetUpload(e, 'og-image')}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => ogImageInputRef.current?.click()}
                      disabled={loading}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Upload
                    </Button>
                  </div>

                  {/* PWA 192 */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded border flex items-center justify-center bg-muted">
                        {branding?.pwa_icon_192_url ? (
                          <img src={getAssetUrl(branding.pwa_icon_192_url)} alt="PWA 192" className="h-8 w-8" />
                        ) : (
                          <span className="text-xs text-muted-foreground">192</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">PWA Icon 192</p>
                        <p className="text-xs text-muted-foreground">192x192px PNG</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={pwaIcon192InputRef}
                      onChange={(e) => handleAssetUpload(e, 'pwa-192')}
                      accept="image/png"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => pwaIcon192InputRef.current?.click()}
                      disabled={loading}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Upload
                    </Button>
                  </div>

                  {/* PWA 512 */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded border flex items-center justify-center bg-muted">
                        {branding?.pwa_icon_512_url ? (
                          <img src={getAssetUrl(branding.pwa_icon_512_url)} alt="PWA 512" className="h-8 w-8" />
                        ) : (
                          <span className="text-xs text-muted-foreground">512</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">PWA Icon 512</p>
                        <p className="text-xs text-muted-foreground">512x512px PNG</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={pwaIcon512InputRef}
                      onChange={(e) => handleAssetUpload(e, 'pwa-512')}
                      accept="image/png"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => pwaIcon512InputRef.current?.click()}
                      disabled={loading}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Non-Super Admin sees locked section */
          <div className="bg-card rounded-xl border border-border p-6 opacity-60">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <Lock className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground">
                  {language === 'el' ? 'Branding & Meta Tags' : 'Branding & Meta Tags'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {language === 'el' 
                    ? 'Μόνο για Super Admin' 
                    : 'Super Admin access required'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Period Management */}
        <div className="bg-card rounded-xl border border-amber-500/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {language === 'el' ? 'Διαχείριση Περιόδου' : 'Period Management'}
            </h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            {language === 'el' 
              ? `Τρέχουσα Περίοδος #${ladder?.period_number || 1}. Όταν η περίοδος τελειώσει, αποθηκεύστε την για να ξεκινήσετε νέα.`
              : `Current Period #${ladder?.period_number || 1}. When the period ends, save it to start a new one.`}
          </p>
          
          {/* Check if period has ended */}
          {(() => {
            const periodEnded = ladder?.period_end_date && new Date(ladder.period_end_date) < new Date();
            return (
              <Button 
                variant={periodEnded ? "default" : "outline"}
                onClick={async () => {
                  if (!periodEnded) {
                    toast.error(language === 'el' 
                      ? 'Η περίοδος δεν έχει λήξει ακόμα. Αλλάξτε την ημερομηνία λήξης πρώτα.'
                      : 'Period has not ended yet. Change the end date first.');
                    return;
                  }
                  setLoading(true);
                  try {
                    const response = await axios.post(
                      `${API_URL}/api/ladder/${ladder.id}/end-period`,
                      {},
                      { headers: getAuthHeaders() }
                    );
                    toast.success(language === 'el' 
                      ? `Περίοδος #${response.data.completed_period.period_number} ολοκληρώθηκε!`
                      : `Period #${response.data.completed_period.period_number} completed!`);
                    window.location.reload();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || t('error'));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className={periodEnded ? "bg-amber-600 hover:bg-amber-700" : ""}
                data-testid="end-period-button"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {language === 'el' ? 'Αποθήκευση & Λήξη Περιόδου' : 'Save & End Period'}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Reset Confirmation Dialog - removed, no longer needed */}
    </div>
  );
}
